import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ajouterPiece, getSession, modifierPiece } from '../api/measurementApi';
import { enregistrerPiece, listerToutesEnAttente, surChangementFile } from '../offline/syncManager';
import { nettoyerSaisieDecimale, versAffichage, versNombreOuNull } from '../utils/number';
import { animerRessort, elastique, projeter, vitesseRecente } from '../utils/spring';
import useBreakpoint from '../utils/useBreakpoint';
import usePresence from '../utils/usePresence';
import { TOUCHES_PAVE } from '../data';
import { Chargement, MessageErreur } from '../components/EtatListe';
import { Bouton, BoutonRetour, BoutonTheme, Capsule, ControleSegmente, Icone } from '../components/ui';
import {
  ACCENT, ACCENT_FOND, ACCENT_BORD, AMBRE, AMBRE_BG, AMBRE_FG, FOND_APP, OMBRE_CARTE, OMBRE_FLOTTANT,
  RAYON_CARTE, RAYON_CHAMP, REMPLISSAGE, REMPLISSAGE_2, ROUGE, ROUGE_BG, ROUGE_FG, SEPARATEUR, SUR_ACCENT,
  SURFACE, TEXTE, TEXTE_DESACTIVE, TEXTE_MUET, TEXTE_SECONDAIRE, VERT, VERT_FG,
} from '../theme';

const CLE_ILLUSTRATION_REPLIEE = 'aqs.illustration-repliee';
const CLE_CLAVIER_BLOQUE = 'aqs.clavier-bloque';

// Gabarits du pavé flottant — servent au calcul de sa position par défaut et au bornage du glisser.
const PAVE_W = 312;
const PAVE_W_REPLIE = 260;
const PAVE_H = 372;
const PAVE_H_REPLIE = 124;

// Hauteur cible de la carte « pièce en cours » : la table des pièces mesurées occupe le reste.
// Plafonnée en pourcentage de la zone disponible pour qu'en 8,7" paysage (800 px de haut) la table
// ne soit jamais réduite à néant.
const H_SAISIE_PORTRAIT = 480;
const H_SAISIE_PAYSAGE = 400;

// Une ligne de cote fait ~58 px : en dessous de cette bande libre au-dessus du pavé, plus aucune
// ligne ne peut être ramenée à la vue, et c'est la gouttière latérale qui prend le relais.
const H_LIGNE_COTE = 58;
// La liste ne descend jamais sous cette largeur : au-delà, la gouttière coûterait plus qu'elle
// ne rapporte et c'est le pavé qu'il faut replier ou déplacer.
const LARGEUR_MIN_LISTE = 300;

function pieceEstComplete(piece, lines) {
  return lines.every((l) => piece.values.some((v) => v.lineId === l.lineId && v.measuredValue != null));
}

// Le référentiel accepte n'importe quel texte dans `code` (on y trouve des phrases entières, ex.
// « ALGUNAS IMÁGENES ESTÁN DISPONIBLES… ») : au-delà de quelques caractères, la pastille n'en
// montre que le début et le texte complet passe sous forme de ligne, jamais en largeur.
const CODE_COURT_MAX = 4;
function codeLong(ligne) {
  return String(ligne.code ?? '').length > CODE_COURT_MAX;
}

// Pastille de code : largeur bornée quoi qu'il arrive, le texte en trop est tronqué.
function stylePastilleCode(largeurMax) {
  return {
    display: 'block', maxWidth: largeurMax, minWidth: 26, padding: '0 5px', overflow: 'hidden',
    textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center', borderRadius: 7, fontWeight: 700,
  };
}

// Libellé limité à deux lignes tant qu'il n'est pas déplié : une opération très longue allonge la
// ligne au lieu d'élargir la colonne.
const LIBELLE_DEUX_LIGNES = { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' };

function valeurPiece(piece, lineId) {
  return piece.values.find((v) => v.lineId === lineId);
}

export default function SaisieScreen({ sessionId, contexteInitial, onRetour, onOuvrirSync, enLigne }) {
  const [session, setSession] = useState(contexteInitial?.session || null);
  const [chargement, setChargement] = useState(!contexteInitial?.session);
  const [erreurChargement, setErreurChargement] = useState(null);

  const [enAttenteLocales, setEnAttenteLocales] = useState([]); // pièces créées hors-ligne, pas encore synchronisées

  const [taille, setTaille] = useState(null);
  const [valeurs, setValeurs] = useState({});
  const [enregistrees, setEnregistrees] = useState({});
  const [actif, setActif] = useState(null);
  const [pave, setPave] = useState(false);
  const [paveReplie, setPaveReplie] = useState(false);
  // null = position par défaut, en bas à droite. `libre` : position non bornée, le temps d'un
  // glisser (résistance élastique au bord) ou du ressort qui le ramène dans la zone visible.
  const [pavePos, setPavePos] = useState(null);
  const [clavierBloque, setClavierBloque] = useState(false);
  const [illustrationOuverte, setIllustrationOuverte] = useState(() => {
    try { return localStorage.getItem(CLE_ILLUSTRATION_REPLIEE) !== '1'; } catch { return true; }
  });
  const [illustrationPleinEcran, setIllustrationPleinEcran] = useState(false);
  const [videoLigne, setVideoLigne] = useState(null);
  const [correction, setCorrection] = useState(null); // { id, size }
  const [soumission, setSoumission] = useState(false);
  const [erreurSoumission, setErreurSoumission] = useState(null);
  // Accusé de réception bref après une validation : le geste a abouti, on le dit, puis on s'efface.
  const [confirmation, setConfirmation] = useState(null);
  // Ligne de la table dont le libellé complet est déplié (toucher l'en-tête de ligne).
  const [ligneDepliee, setLigneDepliee] = useState(null);

  // Onglets : seuls utilisés en mode compact, mais l'état est conservé d'une rotation à l'autre.
  const [onglet, setOnglet] = useState('saisie');

  // Marges rendues à la liste des cotes pour dégager le pavé — voir l'effet de mesure.
  const [gouttiere, setGouttiere] = useState({ gauche: 0, droite: 0, bas: 0 });

  const listeCotesRef = useRef(null);
  const lignesRef = useRef({}); // lineId -> élément de la ligne de cote, pour le recentrage
  const paveRef = useRef(null);
  const arretRessortsRef = useRef([]); // ressorts en vol du pavé, arrêtés si on le rattrape
  const derniereVideoRef = useRef(null);

  // Palier + dimensions de la fenêtre : le pavé flottant est borné sur la zone visible, donc il
  // suit la hauteur réelle (rotation de la tablette, barre d'URL d'Android).
  const { compact, large, portrait, largeur: fenetreW, hauteur: fenetreH } = useBreakpoint();

  useEffect(() => {
    try {
      const v = sessionStorage.getItem(CLE_CLAVIER_BLOQUE) ?? localStorage.getItem(CLE_CLAVIER_BLOQUE);
      if (v === '1') { setClavierBloque(true); setPave(true); }
    } catch { /* navigation privée */ }
  }, []);

  useEffect(() => () => arretRessortsRef.current.forEach((arret) => arret()), []);

  useEffect(() => {
    if (!confirmation) return undefined;
    const minuteur = setTimeout(() => setConfirmation(null), 2200);
    return () => clearTimeout(minuteur);
  }, [confirmation]);

  // ---------- Chargement de la session ----------

  const chargerSession = useCallback(() => {
    setChargement(true);
    setErreurChargement(null);
    getSession(sessionId)
      .then((s) => {
        setSession(s);
        setTaille((t) => t || s.taillesDisponibles?.[0] || null);
      })
      .catch((err) => setErreurChargement(err.message || 'Impossible de charger la campagne de mesure.'))
      .finally(() => setChargement(false));
  }, [sessionId]);

  useEffect(() => {
    if (!session) {
      chargerSession();
    } else if (!taille && session.taillesDisponibles?.length) {
      setTaille(session.taillesDisponibles[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Pièces créées hors-ligne, en attente d'envoi pour CETTE session — rechargées à chaque
  // changement de la file (ajout, envoi réussi, échec).
  useEffect(() => {
    let annule = false;
    function rafraichir() {
      listerToutesEnAttente().then((tous) => {
        if (!annule) setEnAttenteLocales(tous.filter((i) => i.sessionId === sessionId));
      });
    }
    rafraichir();
    const desabonner = surChangementFile(() => {
      rafraichir();
      // Une pièce vient de partir : la session côté serveur a changé (compteurs, statuts), on la
      // relit pour rester la source de vérité — jamais d'incrément local du quota.
      chargerSession();
    });
    return () => { annule = true; desabonner(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const lines = useMemo(() => session?.lines || [], [session]);

  // Pavé applicatif ouvert = clavier système neutralisé : c'est lui qui, sur une tablette Android,
  // recouvre la moitié basse de l'écran et donc le champ en cours de saisie.
  const clavierNatifBloque = clavierBloque || pave;

  useEffect(() => {
    if (pave && document.activeElement?.tagName === 'INPUT') document.activeElement.blur();
  }, [pave]);

  // Le pavé ne doit jamais recouvrir durablement le champ en cours. Deux parades, dans cet ordre :
  //
  //  1. si la liste garde une bande libre AU-DESSUS du pavé, on réserve en bas de la liste de quoi
  //     faire remonter les dernières lignes au-dessus de lui (sans cette réserve, la liste bute sur
  //     sa fin et la dernière cote reste cachée), puis on y ramène la ligne active ;
  //  2. si cette bande ne tient même pas une ligne (pavé déplié sur une liste très courte), on
  //     réserve une GOUTTIÈRE latérale du côté où se trouve le pavé : les deux colonnes de cotes se
  //     resserrent, mais aucune n'est plus cachée.
  //
  // La mesure porte sur la boîte de bordure de la liste, que le padding ne déplace pas : pas de
  // boucle de mise en page entre la réserve et sa mesure.
  const VIDE = { gauche: 0, droite: 0, bas: 0 };
  useLayoutEffect(() => {
    const conteneur = listeCotesRef.current;
    const p = pave ? paveRef.current?.getBoundingClientRect() : null;
    const poser = (n) => setGouttiere((g) => (g.gauche === n.gauche && g.droite === n.droite && g.bas === n.bas ? g : n));
    if (!conteneur || !p) { poser(VIDE); return; }

    const z = conteneur.getBoundingClientRect();
    if (p.left >= z.right || p.right <= z.left || p.top >= z.bottom) { poser(VIDE); return; }

    const bandeLibre = p.top - z.top; // hauteur exploitable au-dessus du pavé
    if (bandeLibre >= H_LIGNE_COTE) {
      poser({ gauche: 0, droite: 0, bas: Math.max(Math.round(z.bottom - p.top) + 8, 0) });
      return;
    }

    const maxi = Math.max(z.width - LARGEUR_MIN_LISTE, 0);
    const aDroite = p.left + p.width / 2 >= z.left + z.width / 2;
    const voulu = Math.max(Math.min(Math.round(aDroite ? z.right - p.left + 8 : p.right - z.left + 8), maxi), 0);
    poser(aDroite ? { gauche: 0, droite: voulu, bas: 0 } : { gauche: voulu, droite: 0, bas: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pave, paveReplie, pavePos, compact, large, portrait, fenetreW, fenetreH, onglet, lines.length]);

  // Ramène la cote active dans la partie visible de la LISTE (pas de la page : la page ne défile
  // plus), en tenant compte du pavé flottant s'il recouvre encore la zone utile.
  useEffect(() => {
    if (!actif) return;
    const conteneur = listeCotesRef.current;
    const ligne = lignesRef.current[actif];
    if (!conteneur || !ligne) return;

    const z = conteneur.getBoundingClientRect();
    const utileGauche = z.left + gouttiere.gauche;
    const utileDroite = z.right - gouttiere.droite;
    let plancher = z.bottom - 6;
    const p = pave ? paveRef.current?.getBoundingClientRect() : null;
    if (p && p.top > z.top && p.left < utileDroite && p.right > utileGauche) {
      plancher = Math.min(plancher, p.top - 6);
    }
    const plafond = z.top + 6;

    const r = ligne.getBoundingClientRect();
    const decalage = r.top < plafond ? r.top - plafond : r.bottom > plancher ? r.bottom - plancher : 0;
    if (decalage) conteneur.scrollBy({ top: decalage, behavior: 'smooth' });
  }, [actif, pave, paveReplie, pavePos, gouttiere]);

  // Fusion pièces confirmées serveur + pièces en attente d'envoi, pour un affichage continu.
  const piecesAffichees = useMemo(() => {
    const piecesServeur = session?.pieces || [];
    const locales = enAttenteLocales.map((item) => ({
      localId: item.localId,
      pieceId: item.pieceId, // correction d'une pièce déjà existante, encore en file
      enAttente: true,
      size: item.payload.size,
      values: item.payload.values,
      erreur: item.erreur,
    }));
    return [...piecesServeur, ...locales];
  }, [session, enAttenteLocales]);

  const nbCompletes = piecesAffichees.filter((p) => p.enAttente ? pieceEstComplete(p, lines) : p.statut !== 'INCOMPLET').length;
  const nbIncompletes = piecesAffichees.length - nbCompletes;

  const quotaFait = piecesAffichees.length;
  const quotaTotal = session?.quantiteMinRequise ?? 0;
  const quotaPct = quotaTotal > 0 ? Math.min(Math.round((quotaFait / quotaTotal) * 100), 100) : 0;

  // ---------- Saisie ----------

  function saisir(lineId, brut) {
    const v = nettoyerSaisieDecimale(brut);
    setValeurs((s) => ({ ...s, [lineId]: v }));
    setEnregistrees((s) => ({ ...s, [lineId]: v.length > 0 }));
    setActif(lineId);
  }

  function deplacer(pas) {
    if (lines.length === 0) return;
    const idx = Math.max(lines.findIndex((l) => l.lineId === actif), 0);
    const suivant = lines[(idx + pas + lines.length) % lines.length];
    setActif(suivant.lineId);
  }

  function touche(k) {
    const lineId = actif || lines[0]?.lineId;
    if (lineId == null) return;
    if (k === '↓') { deplacer(1); return; }
    if (k === 'OK') {
      const cur = valeurs[lineId] || '';
      if (cur) setEnregistrees((s) => ({ ...s, [lineId]: true }));
      deplacer(1);
      return;
    }
    const cur = valeurs[lineId] || '';
    let v = cur;
    if (k === '⌫') v = cur.slice(0, -1);
    else if (k === 'C') v = '';
    else if (k === ',') v = cur.includes(',') ? cur : (cur || '0') + ',';
    else v = cur + k;
    saisir(lineId, v);
  }

  function basculerClavier() {
    setClavierBloque((v) => {
      const nouveau = !v;
      try {
        sessionStorage.setItem(CLE_CLAVIER_BLOQUE, nouveau ? '1' : '0');
        localStorage.setItem(CLE_CLAVIER_BLOQUE, nouveau ? '1' : '0');
      } catch { /* navigation privée */ }
      if (nouveau) {
        setPave(true);
        if (document.activeElement?.tagName === 'INPUT') document.activeElement.blur();
      }
      return nouveau;
    });
  }

  function reinitialiserSaisie() {
    setValeurs({});
    setEnregistrees({});
    setCorrection(null);
    setActif(null);
    setErreurSoumission(null);
  }

  async function valider() {
    if (soumission) return;
    if (!taille) {
      setErreurSoumission('Choisissez une taille avant de valider.');
      return;
    }
    const values = lines
      .map((l) => ({ lineId: l.lineId, measuredValue: versNombreOuNull(valeurs[l.lineId]) }))
      .filter((v) => v.measuredValue != null);
    if (values.length === 0) {
      setErreurSoumission('Saisissez au moins une cote avant de valider.');
      return;
    }

    const payload = { size: taille, values };
    const libelle = correction ? 'Correction enregistrée' : `Pièce n° ${quotaFait + 1} enregistrée`;
    setSoumission(true);
    setErreurSoumission(null);

    try {
      if (correction?.id) {
        const res = await modifierPiece(sessionId, correction.id, payload);
        appliquerResultat(res);
      } else {
        const res = await ajouterPiece(sessionId, payload);
        appliquerResultat(res);
      }
      reinitialiserSaisie();
      signalerSucces(libelle);
    } catch (err) {
      if (err.horsLigne) {
        // Réseau indisponible : on met en file, la pièce apparaît immédiatement en attente et
        // partira dès le retour du réseau. Rien n'est perdu.
        await enregistrerPiece({
          sessionId,
          pieceId: correction?.id || null,
          payload,
          meta: { taille },
        });
        reinitialiserSaisie();
        signalerSucces('Enregistrée sur la tablette — envoi au retour du réseau');
      } else {
        // Refus métier (ex. taille hors référentiel) : on laisse la saisie en place pour corriger.
        setErreurSoumission(err.message || "Impossible d'enregistrer cette pièce.");
      }
    } finally {
      setSoumission(false);
    }
  }

  // Le visuel et la vibration partent sur la même image : la cause (la pièce validée) est évidente.
  function signalerSucces(texte) {
    setConfirmation({ texte, cle: Date.now() });
    try { navigator.vibrate?.(12); } catch { /* non pris en charge */ }
  }

  function appliquerResultat(res) {
    setSession((s) => s && ({
      ...s,
      pieces: mettreAJourPieces(s.pieces, res.piece),
      nbPiecesMesurees: res.compteurs.nbPiecesMesurees,
      nbPiecesConformes: res.compteurs.nbPiecesConformes,
      nbPiecesHorsNorme: res.compteurs.nbPiecesHorsNorme,
      nbPiecesIncompletes: res.compteurs.nbPiecesIncompletes,
      quantiteMinRequise: res.compteurs.quantiteMinRequise,
      quantiteManquante: res.compteurs.quantiteManquante,
      quotaAtteint: res.compteurs.quotaAtteint,
    }));
  }

  function corriger(piece) {
    if (piece.enAttente) return; // pas encore d'id serveur : rien à corriger tant que non synchronisée
    const brut = {};
    piece.values.forEach((v) => { brut[v.lineId] = versAffichage(v.measuredValue); });
    setValeurs(brut);
    setEnregistrees({});
    setTaille(piece.size);
    setCorrection({ id: piece.id, size: piece.size });
    setErreurSoumission(null);
    setOnglet('saisie'); // en mode onglets, la correction se fait dans l'onglet Saisie
  }

  function replierIllustration() {
    setIllustrationOuverte((v) => {
      const nouveau = !v;
      try { localStorage.setItem(CLE_ILLUSTRATION_REPLIEE, nouveau ? '0' : '1'); } catch { /* navigation privée */ }
      return nouveau;
    });
  }

  // ---------- Pavé flottant ----------

  const paveW = paveReplie ? PAVE_W_REPLIE : PAVE_W;
  const paveH = paveReplie ? PAVE_H_REPLIE : PAVE_H;
  const bornes = {
    xMin: 6, yMin: 6,
    xMax: Math.max(fenetreW - paveW - 6, 6),
    yMax: Math.max(fenetreH - paveH - 6, 6),
  };
  const borner = (v, min, max) => Math.min(Math.max(v, min), max);

  // Le bornage est appliqué à l'affichage, pas au stockage : une rotation de la tablette ou un
  // repli du pavé ramènent toujours une position valide sans toucher à l'état. Seule exception :
  // pendant un glisser ou un ressort (`libre`), la position affichée est celle du geste. Le pavé
  // est en `position: absolute` dans la coque de l'écran, qui occupe exactement la fenêtre : les
  // deux repères coïncident.
  const posPave = (() => {
    if (pavePos?.libre) return pavePos;
    if (!pavePos) {
      return { x: Math.max(fenetreW - paveW - 16, 6), y: Math.max(fenetreH - paveH - 96, 6) };
    }
    return { x: borner(pavePos.x, bornes.xMin, bornes.xMax), y: borner(pavePos.y, bornes.yMin, bornes.yMax) };
  })();

  /**
   * Glisser du pavé, comme un objet physique : il reste collé au doigt à l'endroit où on l'a
   * saisi, résiste élastiquement au-delà des bords, puis, au relâché, poursuit sur sa lancée
   * (point d'arrêt projeté depuis la vitesse du doigt) et se pose par un ressort qui hérite de
   * cette vitesse. On peut le rattraper en vol : le geste repart de sa position affichée.
   */
  function glisserPave(e) {
    if (e.target.closest('button')) return; // replier / fermer restent de simples boutons
    e.preventDefault();
    arretRessortsRef.current.forEach((arret) => arret());
    arretRessortsRef.current = [];

    const poignee = e.currentTarget;
    try { poignee.setPointerCapture(e.pointerId); } catch { /* pointeur déjà relâché */ }
    const x0 = e.clientX;
    const y0 = e.clientY;
    const depart = posPave;
    const b = bornes;
    const historique = [{ x: x0, y: y0, t: performance.now() }];
    let courant = { x: depart.x, y: depart.y };

    const suivre = (brut, min, max, dimension) => {
      if (brut < min) return min - elastique(min - brut, dimension);
      if (brut > max) return max + elastique(brut - max, dimension);
      return brut;
    };

    function bouge(ev) {
      historique.push({ x: ev.clientX, y: ev.clientY, t: performance.now() });
      if (historique.length > 10) historique.shift();
      courant = {
        x: suivre(depart.x + (ev.clientX - x0), b.xMin, b.xMax, fenetreW),
        y: suivre(depart.y + (ev.clientY - y0), b.yMin, b.yMax, fenetreH),
      };
      setPavePos({ ...courant, libre: true });
    }

    function fin() {
      poignee.removeEventListener('pointermove', bouge);
      poignee.removeEventListener('pointerup', fin);
      poignee.removeEventListener('pointercancel', fin);

      const { vx, vy } = vitesseRecente(historique);
      const cible = {
        x: borner(courant.x + projeter(vx), b.xMin, b.xMax),
        y: borner(courant.y + projeter(vy), b.yMin, b.yMax),
      };
      // Un léger rebond seulement si le geste a été lancé ; sinon le pavé se pose sans dépasser.
      const lance = Math.hypot(vx, vy) > 300;
      const reglage = { reponse: 0.42, amortissement: lance ? 0.86 : 1 };
      const pos = { ...courant };
      let restants = 2;
      const terminer = () => {
        restants -= 1;
        if (restants === 0) { arretRessortsRef.current = []; setPavePos({ x: cible.x, y: cible.y }); }
      };
      arretRessortsRef.current = [
        animerRessort({ de: courant.x, vers: cible.x, vitesse: vx, ...reglage, surImage: (x) => { pos.x = x; setPavePos({ ...pos, libre: true }); }, surFin: terminer }),
        animerRessort({ de: courant.y, vers: cible.y, vitesse: vy, ...reglage, surImage: (y) => { pos.y = y; setPavePos({ ...pos, libre: true }); }, surFin: terminer }),
      ];
    }

    poignee.addEventListener('pointermove', bouge);
    poignee.addEventListener('pointerup', fin);
    poignee.addEventListener('pointercancel', fin);
  }

  const enAttenteCount = enAttenteLocales.filter((i) => !i.erreur).length;
  const enErreurCount = enAttenteLocales.filter((i) => i.erreur).length;

  const codeActif = actif || lines[0]?.lineId;
  const idxActif = Math.max(lines.findIndex((l) => l.lineId === codeActif), 0);
  const coteActive = lines[idxActif];

  const vueSaisie = !compact || onglet === 'saisie';
  const vueTable = !compact || onglet === 'table';

  // Entrées ET sorties animées : le pavé rétrécit vers où il était, la vidéo et la planche
  // repartent par où elles sont arrivées.
  const presencePave = usePresence(pave && vueSaisie, 200);
  const presenceVideo = usePresence(!!videoLigne, 260);
  const presencePlanche = usePresence(illustrationPleinEcran, 260);
  if (videoLigne) derniereVideoRef.current = videoLigne;
  const videoAffichee = videoLigne || derniereVideoRef.current;

  if (chargement && !session) {
    return (
      <div style={{ height: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: FOND_APP }}>
        <Chargement texte="Chargement de la campagne de mesure…" />
      </div>
    );
  }

  if (erreurChargement && !session) {
    return (
      <div style={{ height: '100dvh', overflow: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, background: FOND_APP, padding: 24 }}>
        <div style={{ width: '100%', maxWidth: 520 }}><MessageErreur texte={erreurChargement} onReessayer={chargerSession} /></div>
        <BoutonRetour libelle="Retour à la liste" onClick={onRetour} />
      </div>
    );
  }

  const illustrationVide = !session?.illustrationUrl;
  const illustrationPdf = session?.illustrationContentType === 'application/pdf';
  const largeurCote = compact ? 150 : 216;

  // Le cadre illustration ne tient dans le flux qu'à partir de 1280 px de large ET 900 px de haut :
  // sur une 10,1"/8,7" paysage (800 px de haut), la rangée qu'il occuperait est précisément celle
  // dont la carte de saisie et la table ont besoin. Partout ailleurs, la planche s'ouvre en plein
  // écran depuis le bouton « Planche » — le seul format lisible sur une tablette, de toute façon.
  // Sans planche déposée, pas de cadre du tout : un encart vide ne mérite pas 150 px de hauteur.
  const bandeauIllustration = large && fenetreH >= 900 && !illustrationVide;

  // Zone principale : une colonne, deux rangées — la carte de saisie puis la table. La part de la
  // carte de saisie est plafonnée en pourcentage pour que la table garde toujours de la place.
  const hSaisie = portrait ? H_SAISIE_PORTRAIT : H_SAISIE_PAYSAGE;
  const partSaisie = bandeauIllustration && illustrationOuverte ? '42%' : '58%';
  const rangeesPrincipales = compact
    ? 'minmax(0, 1fr)'
    : `minmax(0, min(${hSaisie}px, ${partSaisie})) minmax(0, 1fr)`;
  const rangees = bandeauIllustration ? `auto ${rangeesPrincipales}` : rangeesPrincipales;

  const boutonPlanche = (
    <Bouton
      variante="gris"
      onClick={() => setIllustrationPleinEcran(true)}
      disabled={illustrationVide}
      icone={<Icone.Planche taille={19} />}
      aria-label="Planche des cotes"
      style={{ fontSize: 15, padding: compact ? 0 : '0 14px' }}
    >
      {!compact && 'Planche'}
    </Bouton>
  );

  const barreProgression = (
    <div style={{ height: 6, borderRadius: 3, background: REMPLISSAGE, overflow: 'hidden' }}>
      <div style={{ height: '100%', borderRadius: 3, background: session?.quotaAtteint ? VERT : ACCENT, width: quotaPct + '%', transition: 'width 600ms var(--aqs-courbe), background-color 300ms ease' }} />
    </div>
  );

  const carte = {
    minWidth: 0, minHeight: 0, background: SURFACE, borderRadius: RAYON_CARTE, boxShadow: OMBRE_CARTE,
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
  };

  const etatSync = enErreurCount ? ROUGE : (!enLigne || enAttenteCount ? AMBRE : VERT);

  return (
    <div style={{ position: 'relative', height: '100dvh', overflow: 'hidden', background: FOND_APP, color: TEXTE, display: 'flex', flexDirection: 'column' }}>

      {/* Barre de navigation : retour, identité de la commande, avancement ; onglets sous le
          seuil compact. Matériau translucide, comme toute la chrome de l'app. */}
      <div className="aqs-materiau" style={{ flex: 'none', zIndex: 10, borderBottom: `0.5px solid ${SEPARATEUR}` }}>
        <div style={{ padding: compact ? '6px 14px 8px' : '8px 18px', display: 'grid', gridTemplateColumns: large ? 'auto minmax(0, 1fr) 340px' : 'auto minmax(0, 1fr)', alignItems: 'center', columnGap: 14, rowGap: 6 }}>
          <BoutonRetour libelle={compact ? 'Liste' : 'Liste des lots'} onClick={onRetour} />

          <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                <span style={{ flex: 'none', fontSize: 18, fontWeight: 700, letterSpacing: '-0.015em', whiteSpace: 'nowrap' }}>{session?.numeroCommande}</span>
                <Capsule ton="accent">{session?.voletLibelle || session?.volet}</Capsule>
              </div>
              <div style={{ minWidth: 0, fontSize: 14, color: TEXTE_SECONDAIRE, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{session?.modeleName}</div>
            </div>
            {!bandeauIllustration && boutonPlanche}
            <BoutonTheme hauteur={44} />
          </div>

          {large && (
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontSize: 14, color: TEXTE_SECONDAIRE }}><span style={{ fontSize: 20, fontWeight: 700, color: TEXTE, letterSpacing: '-0.02em' }}>{quotaFait}</span> / {quotaTotal || '—'}</span>
                <span style={{ fontSize: 13, color: TEXTE_SECONDAIRE, whiteSpace: 'nowrap' }}>pièces mesurées</span>
              </div>
              <div style={{ marginTop: 6 }}>{barreProgression}</div>
            </div>
          )}

          {/* Sous 1280 px, l'avancement passe sous l'identité, sur toute la largeur de l'en-tête :
              lui garder une colonne écraserait le nom du modèle à quelques caractères. */}
          {!large && (
            <div style={{ gridColumn: '1 / -1', minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ flex: 'none', fontSize: 14, color: TEXTE_SECONDAIRE, whiteSpace: 'nowrap' }}><strong style={{ color: TEXTE, fontWeight: 700 }}>{quotaFait}</strong> / {quotaTotal || '—'} pièces</span>
              <div style={{ flex: 1, minWidth: 0 }}>{barreProgression}</div>
            </div>
          )}
        </div>

        {compact && (
          <div style={{ padding: '0 14px 10px' }}>
            <ControleSegmente
              options={[
                { valeur: 'saisie', libelle: 'Saisie' },
                { valeur: 'table', libelle: `Pièces mesurées · ${piecesAffichees.length}` },
              ]}
              valeur={onglet}
              onChange={setOnglet}
            />
          </div>
        )}
      </div>

      {/* Bandeaux d'état : ils doivent rester visibles pendant la saisie */}
      {!enLigne && (
        <div className="aqs-apparition" style={{ flex: 'none', background: AMBRE_BG, padding: '10px 18px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ color: AMBRE, display: 'inline-flex' }}><Icone.Nuage taille={20} /></span>
          <span className="aqs-para" style={{ minWidth: 0, fontSize: 15, color: AMBRE_FG }}>
            <strong style={{ fontWeight: 650 }}>Mode hors-ligne.</strong> Vos mesures sont enregistrées sur la tablette et envoyées dès le retour du réseau.
          </span>
        </div>
      )}
      {enErreurCount > 0 && (
        <div className="aqs-apparition" style={{ flex: 'none', background: ROUGE_BG, padding: '8px 18px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ color: ROUGE, display: 'inline-flex' }}><Icone.Attention taille={20} /></span>
          <span style={{ minWidth: 0, fontSize: 15, fontWeight: 600, color: ROUGE_FG }}>{enErreurCount} pièce(s) refusée(s) par le serveur</span>
          <Bouton variante="texte" onClick={onOuvrirSync} style={{ marginLeft: 'auto', color: ROUGE_FG }}>Voir le détail</Bouton>
        </div>
      )}

      {/* Zone principale : une colonne, deux rangées. Chaque rangée défile pour elle-même. */}
      <div style={{ flex: 1, minHeight: 0, padding: compact ? 12 : '14px 18px', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gridTemplateRows: rangees, gap: 14 }}>

        {bandeauIllustration && (
          <div style={carte}>
            <div style={{ padding: '4px 10px 4px 6px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <button type="button" className="aqs-pressable aqs-pressable-leger" onClick={replierIllustration} aria-expanded={illustrationOuverte} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 44, padding: '0 8px', border: 'none', background: 'none', fontSize: 16, fontWeight: 650, color: TEXTE, cursor: 'pointer' }}>
                <span style={{ display: 'inline-flex', color: TEXTE_MUET, transform: `rotate(${illustrationOuverte ? 90 : 0}deg)`, transition: 'transform 300ms var(--aqs-courbe)' }}><Icone.ChevronDroite taille={18} /></span>
                Illustration des cotes
              </button>
              <span className="aqs-para" style={{ flex: 1, minWidth: 120, fontSize: 14, color: TEXTE_MUET }}>Chaque lettre de la colonne Code y est repérée sur le vêtement.</span>
              <Bouton variante="gris" onClick={() => setIllustrationPleinEcran(true)} icone={<Icone.Agrandir taille={17} />} style={{ fontSize: 15 }}>Plein écran</Bouton>
            </div>
            {illustrationOuverte && (
              <div className="aqs-apparition" style={{ padding: '0 12px 12px' }}>
                <div style={{ height: portrait ? 132 : 108, borderRadius: 12, background: REMPLISSAGE_2, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                  {!illustrationPdf && <img src={session.illustrationUrl} alt="Planche des cotes" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />}
                  {illustrationPdf && <iframe title="Planche des cotes" src={session.illustrationUrl} style={{ width: '100%', height: '100%', border: 'none' }} />}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Pièce en cours */}
        {vueSaisie && (
          <div style={carte}>
            <div style={{ flex: 'none', padding: '12px 16px 10px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span className="aqs-titre-2" style={{ fontSize: 19 }}>
                {correction ? `Correction pièce n° ${(session?.pieces || []).find((p) => p.id === correction.id)?.numeroPiece ?? ''}` : `Pièce n° ${quotaFait + 1}`}
              </span>
              {correction && <Capsule ton="accent">Correction</Capsule>}
              <span style={{ flex: 'none', fontSize: 14, color: TEXTE_MUET }}>Taille</span>
              <div style={{ flex: 1, minWidth: 180, display: 'flex', gap: 6, overflowX: 'auto', padding: 2 }}>
                {(session?.taillesDisponibles || []).map((t) => {
                  const on = taille === t;
                  return (
                    <button
                      key={t}
                      type="button"
                      className="aqs-pressable"
                      aria-pressed={on}
                      onClick={() => setTaille(t)}
                      style={{ flex: 'none', minWidth: 48, height: 40, padding: '0 12px', borderRadius: 20, fontSize: 16, fontWeight: 650, cursor: 'pointer', border: 'none', background: on ? ACCENT : REMPLISSAGE, color: on ? SUR_ACCENT : TEXTE }}
                    >
                      {t}
                    </button>
                  );
                })}
                {(!session?.taillesDisponibles || session.taillesDisponibles.length === 0) && (
                  <span style={{ fontSize: 14, color: TEXTE_MUET, alignSelf: 'center' }}>Aucune taille connue.</span>
                )}
              </div>
            </div>

            {/* Barre de navigation entre cotes — sa propre ligne, pour des cibles tactiles de 44 px */}
            <div style={{ flex: 'none', margin: '0 16px', padding: '8px 0', borderTop: `0.5px solid ${SEPARATEUR}`, borderBottom: `0.5px solid ${SEPARATEUR}`, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ flex: 'none', fontSize: 14, fontWeight: 600, color: TEXTE_SECONDAIRE, whiteSpace: 'nowrap' }}>Cote {lines.length ? idxActif + 1 : 0} sur {lines.length}</span>
              <span style={{ flex: 1, minWidth: 80, fontSize: 14, color: TEXTE_MUET, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{coteActive?.label}</span>
              <Bouton variante="gris" onClick={() => deplacer(-1)} aria-label="Cote précédente" icone={<Icone.ChevronHaut taille={20} />} style={{ borderRadius: 22 }} />
              <Bouton variante="gris" onClick={() => deplacer(1)} aria-label="Cote suivante" icone={<Icone.ChevronBas taille={20} />} style={{ borderRadius: 22 }} />
              <Bouton
                variante={pave ? 'plein' : 'gris'}
                onClick={() => setPave((v) => !v)}
                disabled={clavierBloque}
                aria-pressed={pave}
                icone={<Icone.Pave taille={19} />}
                title={clavierBloque ? 'Le pavé reste ouvert tant que le clavier de la tablette est bloqué' : undefined}
                style={{ fontSize: 15, opacity: clavierBloque ? 0.7 : 1 }}
              >
                Pavé numérique
              </Bouton>
              <Bouton
                variante={clavierBloque ? 'plein' : 'gris'}
                onClick={basculerClavier}
                aria-pressed={clavierBloque}
                icone={<Icone.Clavier taille={20} />}
                title={clavierBloque ? 'Clavier tablette bloqué — toucher pour le réactiver' : 'Bloquer le clavier de la tablette'}
                aria-label={clavierBloque ? 'Clavier tablette bloqué — toucher pour le réactiver' : 'Bloquer le clavier de la tablette'}
              />
            </div>

            {/* Grille des cotes : 2 colonnes, défilement interne à la carte */}
            <div
              ref={listeCotesRef}
              style={{
                flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden',
                paddingTop: 8, paddingBottom: 10 + gouttiere.bas,
                paddingLeft: 10 + gouttiere.gauche, paddingRight: 10 + gouttiere.droite,
                display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', alignContent: 'start',
                columnGap: 10, rowGap: 4, transition: 'padding 320ms var(--aqs-courbe)',
              }}
            >
              {lines.map((l) => {
                const on = actif === l.lineId;
                const v = valeurs[l.lineId] || '';
                return (
                  <div
                    key={l.lineId}
                    ref={(el) => { if (el) lignesRef.current[l.lineId] = el; else delete lignesRef.current[l.lineId]; }}
                    onClick={() => setActif(l.lineId)}
                    style={{
                      minWidth: 0, display: 'grid', gridTemplateColumns: `32px 34px minmax(0, 1fr) ${compact ? 90 : 100}px`, alignItems: 'center',
                      gap: 8, padding: '5px 6px 5px 8px', borderRadius: 12, background: on ? ACCENT_FOND : 'transparent',
                      transition: 'background-color 200ms ease',
                    }}
                  >
                    <span title={l.code} style={{ ...stylePastilleCode(32), height: 30, lineHeight: '30px', padding: '0 3px', fontSize: 14, borderRadius: 8, background: on ? ACCENT : REMPLISSAGE, color: on ? SUR_ACCENT : TEXTE, transition: 'background-color 200ms ease, color 200ms ease' }}>{l.code}</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                      {l.videoUrl && (
                        <button type="button" className="aqs-pressable" onClick={(e) => { e.stopPropagation(); setVideoLigne(l); }} aria-label={`Voir le tutoriel vidéo — ${l.label}`} style={{ width: 34, height: 44, padding: 0, borderRadius: 17, border: 'none', background: 'none', color: ACCENT, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ width: 30, height: 30, borderRadius: '50%', background: ACCENT_FOND, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><Icone.Lecture taille={15} /></span>
                        </button>
                      )}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      {/* La cote active se déplie en entier ; les autres restent sur deux lignes. */}
                      <div className="aqs-para" style={{ fontSize: 15, fontWeight: on ? 600 : 500, lineHeight: 1.25, overflowWrap: 'anywhere', ...(on ? null : LIBELLE_DEUX_LIGNES) }}>
                        {codeLong(l) && <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: TEXTE_SECONDAIRE }}>{l.code}</span>}
                        {l.label}
                      </div>
                      <div style={{ minHeight: 16, fontSize: 12, lineHeight: '16px', color: VERT_FG, display: 'flex', alignItems: 'center', gap: 3 }}>
                        {enregistrees[l.lineId] && <><Icone.Coche taille={12} /> enregistré</>}
                      </div>
                    </div>
                    <input
                      type="text"
                      inputMode={clavierNatifBloque ? 'none' : 'decimal'}
                      readOnly={clavierNatifBloque}
                      value={v}
                      placeholder="—"
                      aria-label={l.label}
                      onChange={(e) => saisir(l.lineId, e.target.value)}
                      onFocus={() => setActif(l.lineId)}
                      style={{
                        height: 48, width: '100%', minWidth: 0, textAlign: 'center', fontSize: 20, fontWeight: 600, borderRadius: RAYON_CHAMP,
                        background: on ? SURFACE : REMPLISSAGE_2, color: TEXTE, outline: 'none',
                        border: `2px solid ${on ? ACCENT : 'transparent'}`, boxShadow: on ? `0 0 0 4px ${ACCENT_BORD}` : 'none',
                        transition: 'border-color 160ms ease, box-shadow 200ms ease, background-color 160ms ease',
                      }}
                    />
                  </div>
                );
              })}
              {lines.length === 0 && <div style={{ gridColumn: '1 / -1', padding: '20px 8px', fontSize: 15, color: TEXTE_MUET }}>Aucune cote à mesurer pour ce modèle.</div>}
            </div>

            {clavierNatifBloque && (
              <div style={{ flex: 'none', padding: '6px 16px 10px', fontSize: 13, color: TEXTE_MUET }}>
                Clavier de la tablette neutralisé — la saisie se fait avec le pavé numérique flottant.
              </div>
            )}

            {erreurSoumission && (
              <div style={{ flex: 'none', padding: '0 14px 12px' }}>
                <MessageErreur texte={erreurSoumission} />
              </div>
            )}
          </div>
        )}

        {/* Pièces mesurées */}
        {vueTable && (
          <div style={carte}>
            <div style={{ flex: 'none', padding: '12px 16px', borderBottom: `0.5px solid ${SEPARATEUR}`, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span className="aqs-titre-2" style={{ fontSize: 19, marginRight: 4 }}>Pièces mesurées</span>
              <Capsule ton="vert" point>{nbCompletes} complètes</Capsule>
              <Capsule ton="orange" point>{nbIncompletes} incomplètes</Capsule>
              <span className="aqs-para" style={{ minWidth: 0, marginLeft: 'auto', fontSize: 13, color: TEXTE_MUET }}>Touchez une pièce pour la corriger</span>
            </div>
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ position: 'sticky', left: 0, top: 0, zIndex: 4, background: SURFACE, borderRight: `0.5px solid ${SEPARATEUR}`, borderBottom: `0.5px solid ${SEPARATEUR}`, padding: '8px 14px', textAlign: 'left', fontSize: 13, fontWeight: 500, color: TEXTE_MUET, width: largeurCote, minWidth: largeurCote }}>Cote</th>
                    {piecesAffichees.map((p, idx) => {
                      const complete = p.enAttente ? pieceEstComplete(p, lines) : p.statut !== 'INCOMPLET';
                      const enCorrection = correction?.id === p.id;
                      const pastille = p.erreur ? ROUGE : (p.enAttente || !complete ? AMBRE : VERT);
                      return (
                        <th key={p.id ?? `local-${p.localId}`} style={{ position: 'sticky', top: 0, zIndex: 3, borderBottom: `0.5px solid ${SEPARATEUR}`, padding: 3, minWidth: 88, background: SURFACE }}>
                          <button
                            type="button"
                            className="aqs-pressable aqs-apparition"
                            onClick={() => corriger(p)}
                            disabled={p.enAttente}
                            style={{ width: '100%', minHeight: 44, padding: '6px 6px', border: 'none', borderRadius: 10, background: enCorrection ? ACCENT_FOND : 'none', cursor: p.enAttente ? 'default' : 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, color: TEXTE }}
                          >
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 15, fontWeight: 700 }}>
                              <span style={{ width: 7, height: 7, borderRadius: '50%', background: pastille }} />
                              {p.numeroPiece ? `P${p.numeroPiece}` : `#${idx + 1}`}
                            </span>
                            <span style={{ fontSize: 13, color: TEXTE_SECONDAIRE }}>{p.size}</span>
                            {p.enAttente && <span style={{ fontSize: 12, whiteSpace: 'nowrap', color: p.erreur ? ROUGE_FG : AMBRE_FG }}>{p.erreur ? 'refusée' : 'en attente'}</span>}
                          </button>
                        </th>
                      );
                    })}
                    <th style={{ position: 'sticky', top: 0, zIndex: 3, background: SURFACE, borderBottom: `0.5px solid ${SEPARATEUR}`, width: '100%' }} />
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l) => {
                    const depliee = ligneDepliee === l.lineId;
                    return (
                    <tr key={l.lineId}>
                      {/* Colonne Cote à largeur FIXE (width = min = max) : un libellé ou un code
                          très long ne peut plus repousser les colonnes des pièces hors de l'écran.
                          Il passe sur deux lignes, et un toucher le déplie en entier. */}
                      <th style={{ position: 'sticky', left: 0, zIndex: 1, background: SURFACE, borderRight: `0.5px solid ${SEPARATEUR}`, borderBottom: `0.5px solid ${SEPARATEUR}`, padding: 0, textAlign: 'left', fontWeight: 500, width: largeurCote, minWidth: largeurCote, maxWidth: largeurCote }}>
                        <button
                          type="button"
                          onClick={() => setLigneDepliee(depliee ? null : l.lineId)}
                          aria-expanded={depliee}
                          title={`${l.code} — ${l.label}`}
                          style={{ width: '100%', minHeight: 40, padding: '7px 12px', border: 'none', background: depliee ? ACCENT_FOND : 'none', display: 'flex', alignItems: 'flex-start', gap: 9, textAlign: 'left', color: TEXTE, cursor: 'pointer', font: 'inherit', fontWeight: 500 }}
                        >
                          <span style={{ ...stylePastilleCode(44), flex: 'none', height: 24, lineHeight: '24px', background: REMPLISSAGE, fontSize: 13 }}>{l.code}</span>
                          <span style={{ flex: 1, minWidth: 0, fontSize: 14, lineHeight: 1.3, paddingTop: 2, overflowWrap: 'anywhere', ...(depliee ? null : LIBELLE_DEUX_LIGNES) }}>
                            {depliee && codeLong(l) && <span style={{ display: 'block', fontWeight: 600 }}>{l.code}</span>}
                            {l.label}
                          </span>
                        </button>
                      </th>
                      {piecesAffichees.map((p) => {
                        const val = valeurPiece(p, l.lineId);
                        const v = val?.measuredValue;
                        const statut = val?.statut;
                        const couleur = statut === 'OK' ? VERT_FG : statut === 'NOK' ? ROUGE_FG : TEXTE;
                        return (
                          <td key={p.id ?? `local-${p.localId}`} style={{ borderBottom: `0.5px solid ${SEPARATEUR}`, padding: '8px 6px', textAlign: 'center', fontSize: 16, fontWeight: statut === 'NOK' ? 700 : 550, background: statut === 'NOK' ? ROUGE_BG : (v != null ? 'transparent' : REMPLISSAGE_2), color: v != null ? couleur : TEXTE_DESACTIVE }}>
                            {v != null ? versAffichage(v) : '—'}
                          </td>
                        );
                      })}
                      <td style={{ borderBottom: `0.5px solid ${SEPARATEUR}` }} />
                    </tr>
                    );
                  })}
                  {lines.length === 0 && (
                    <tr><td style={{ padding: '20px 12px', fontSize: 15, color: TEXTE_MUET }}>—</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Barre d'action : dernière rangée de la coque, jamais recouverte par le pavé */}
      <div className="aqs-materiau" style={{ flex: 'none', position: 'relative', zIndex: 10, borderTop: `0.5px solid ${SEPARATEUR}`, padding: compact ? '10px 12px' : '10px 18px', display: 'flex', alignItems: 'center', gap: 10 }}>
        {confirmation && (
          <div key={confirmation.cle} className="aqs-flottant" role="status" style={{ position: 'absolute', left: '50%', bottom: 'calc(100% + 12px)', transform: 'translateX(-50%)', pointerEvents: 'none' }}>
            <div className="aqs-materiau-epais" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 44, padding: '0 18px', borderRadius: 22, boxShadow: OMBRE_FLOTTANT, fontSize: 15, fontWeight: 600, whiteSpace: 'nowrap' }}>
              <span style={{ color: VERT, display: 'inline-flex' }}><Icone.Coche taille={18} /></span>
              {confirmation.texte}
            </div>
          </div>
        )}
        <Bouton variante="gris" hauteur={54} onClick={reinitialiserSaisie} disabled={soumission} style={{ fontSize: 17, padding: '0 20px', borderRadius: 14 }}>
          {correction ? 'Abandonner' : 'Effacer'}
        </Bouton>
        <Bouton
          variante={correction ? 'plein' : 'succes'}
          hauteur={54}
          onClick={valider}
          disabled={soumission || !taille}
          style={{ flex: 1, minWidth: 0, fontSize: 18, borderRadius: 14, opacity: soumission ? 0.7 : undefined }}
        >
          {soumission ? 'Enregistrement…' : correction ? 'Enregistrer la correction' : 'Valider la pièce'}
        </Bouton>
        {/* Badge de synchronisation : permanent en bas à droite, informatif, jamais bloquant */}
        <button
          type="button"
          className="aqs-pressable"
          onClick={onOuvrirSync}
          style={{ flex: 'none', height: 44, padding: '0 14px', borderRadius: 22, border: 'none', background: REMPLISSAGE, display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600, color: TEXTE_SECONDAIRE, cursor: 'pointer', whiteSpace: 'nowrap' }}
        >
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: etatSync, boxShadow: `0 0 0 3px color-mix(in srgb, ${etatSync} 22%, transparent)` }} />
          {!enLigne
            ? (compact ? `Hors ligne · ${enAttenteCount}` : `Hors ligne · ${enAttenteCount} en attente`)
            : enAttenteCount
              ? (compact ? `Envoi · ${enAttenteCount}` : `Envoi en cours · ${enAttenteCount} en attente`)
              : (compact ? 'En ligne' : 'En ligne · tout est envoyé')}
        </button>
      </div>

      {/* Pavé numérique flottant : posé au-dessus de la carte de saisie, déplaçable au doigt et
          repliable. Borné sur la coque, qui occupe exactement la zone visible. */}
      {presencePave.monte && (
        <div
          ref={paveRef}
          className={`aqs-flottant aqs-materiau-epais${presencePave.sortant ? ' aqs-sortie' : ''}`}
          style={{
            position: 'absolute', left: posPave.x, top: posPave.y, zIndex: 60, width: paveW, borderRadius: 24,
            boxShadow: OMBRE_FLOTTANT, overflow: 'hidden', transformOrigin: 'bottom right',
            border: `0.5px solid ${SEPARATEUR}`, pointerEvents: presencePave.sortant ? 'none' : undefined,
          }}
        >
          <div onPointerDown={glisserPave} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 8, padding: '12px 10px 4px 14px', cursor: 'grab', touchAction: 'none', userSelect: 'none' }}>
            <span aria-hidden="true" style={{ position: 'absolute', top: 6, left: '50%', width: 36, height: 5, marginLeft: -18, borderRadius: 3, background: TEXTE_DESACTIVE }} />
            <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: TEXTE_MUET, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Glisser pour déplacer</span>
            <Bouton variante="gris" hauteur={36} onClick={() => setPaveReplie((v) => !v)} aria-label={paveReplie ? 'Déplier le pavé' : 'Replier le pavé'} icone={paveReplie ? <Icone.ChevronHaut taille={18} /> : <Icone.Reduire taille={18} />} style={{ borderRadius: 18 }} />
            <Bouton
              variante="gris"
              hauteur={36}
              onClick={() => setPave(false)}
              disabled={clavierBloque}
              aria-label="Fermer le pavé"
              title={clavierBloque ? 'Le pavé reste ouvert tant que le clavier de la tablette est bloqué' : 'Fermer le pavé'}
              icone={<Icone.Croix taille={16} />}
              style={{ borderRadius: 18 }}
            />
          </div>
          <div style={{ padding: '6px 10px 10px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: 52, padding: '0 12px', borderRadius: 14, background: SURFACE, boxShadow: `inset 0 0 0 2px ${ACCENT}` }}>
              <span title={coteActive?.code} style={{ ...stylePastilleCode(110), flex: 'none', height: 30, lineHeight: '30px', minWidth: 30, borderRadius: 8, background: ACCENT, color: SUR_ACCENT, fontSize: 15 }}>{coteActive?.code || '—'}</span>
              <span style={{ flex: 1, minWidth: 0, textAlign: 'right', fontSize: 28, fontWeight: 600, letterSpacing: '-0.02em', overflow: 'hidden', whiteSpace: 'nowrap' }}>{(codeActif != null && valeurs[codeActif]) || <span style={{ color: TEXTE_DESACTIVE }}>—</span>}</span>
              <span style={{ flex: 'none', fontSize: 15, color: TEXTE_MUET }}>cm</span>
            </div>
            {!paveReplie && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gridAutoRows: 52, gap: 7 }}>
                {TOUCHES_PAVE.map((k) => {
                  const ok = k === 'OK';
                  const suiv = k === '↓';
                  const gris = k === '⌫' || k === 'C';
                  return (
                    <button
                      key={k}
                      type="button"
                      className={`aqs-pressable${ok ? ' aqs-pressable-plein' : ''}`}
                      onClick={() => touche(k)}
                      aria-label={suiv ? 'Cote suivante' : k === '⌫' ? 'Effacer le dernier chiffre' : k === 'C' ? 'Vider la cote' : undefined}
                      style={{
                        minWidth: 0, borderRadius: 14, border: 'none', cursor: 'pointer',
                        background: ok ? VERT : suiv ? ACCENT_FOND : gris ? REMPLISSAGE : SURFACE,
                        color: ok ? '#FFFFFF' : suiv ? ACCENT : TEXTE,
                        boxShadow: ok || suiv || gris ? 'none' : '0 1px 0 rgba(0,0,0,0.08)',
                        fontSize: ok ? 18 : gris || suiv ? 20 : 24, fontWeight: ok ? 650 : 500,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      {suiv ? <Icone.ChevronBas taille={22} /> : k}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Lecteur vidéo tutoriel, non bloquant : il naît du bas à droite et y repart. */}
      {presenceVideo.monte && videoAffichee && (
        <div
          className={`aqs-flottant aqs-materiau-epais${presenceVideo.sortant ? ' aqs-sortie' : ''}`}
          style={{ position: 'absolute', right: 16, bottom: 92, zIndex: 50, width: Math.min(460, fenetreW - 32), borderRadius: 22, border: `0.5px solid ${SEPARATEUR}`, boxShadow: OMBRE_FLOTTANT, overflow: 'hidden', transformOrigin: 'bottom right' }}
        >
          <div style={{ padding: '10px 10px 10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span title={videoAffichee.code} style={{ ...stylePastilleCode(90), flex: 'none', height: 30, lineHeight: '30px', borderRadius: 8, background: ACCENT, color: SUR_ACCENT, fontSize: 14 }}>{videoAffichee.code}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: TEXTE_MUET }}>Comment mesurer</div>
              <div style={{ fontSize: 15, fontWeight: 650, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{videoAffichee.label}</div>
            </div>
            <Bouton variante="gris" hauteur={36} onClick={() => setVideoLigne(null)} aria-label="Fermer la vidéo" icone={<Icone.Croix taille={16} />} style={{ borderRadius: 18 }} />
          </div>
          <video controls preload="metadata" src={videoAffichee.videoUrl} style={{ width: '100%', height: 240, background: '#000', display: 'block' }} />
        </div>
      )}

      {/* Illustration en plein écran : voile qui assombrit la saisie, planche posée dessus. */}
      {presencePlanche.monte && !illustrationVide && (
        <div
          onClick={() => setIllustrationPleinEcran(false)}
          className={`aqs-voile${presencePlanche.sortant ? ' aqs-sortie' : ''}`}
          style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(0,0,0,0.82)', WebkitBackdropFilter: 'blur(12px)', backdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: compact ? 16 : 32 }}
        >
          <div className={`aqs-feuille${presencePlanche.sortant ? ' aqs-sortie' : ''}`} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {illustrationPdf ? (
              <iframe title="Planche des cotes" src={session.illustrationUrl} style={{ width: '100%', height: '100%', border: 'none', background: '#fff', borderRadius: 16 }} onClick={(e) => e.stopPropagation()} />
            ) : (
              <img src={session.illustrationUrl} alt="Planche des cotes" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 12 }} onClick={(e) => e.stopPropagation()} />
            )}
          </div>
          <Bouton
            variante="gris"
            onClick={() => setIllustrationPleinEcran(false)}
            aria-label="Fermer la planche"
            icone={<Icone.Croix taille={18} />}
            style={{ position: 'absolute', top: 16, right: 16, borderRadius: 22, background: 'rgba(255,255,255,0.18)', color: '#FFFFFF' }}
          />
        </div>
      )}
    </div>
  );
}

function mettreAJourPieces(pieces, piece) {
  const existe = pieces.some((p) => p.id === piece.id);
  return existe ? pieces.map((p) => (p.id === piece.id ? piece : p)) : [...pieces, piece];
}
